import classNames from 'classnames';
import * as React from 'react';
import Link from '../../../common/components/core/Link';

const DLA_URL = 'https://docs.branch.io/pages/deep-linked-ads';

const ADWORDS_UAC_GUIDE_LINK = `${DLA_URL}/google-uac/`;
const ADWORDS_CONVERSION_GUIDE_LINK = `${DLA_URL}/google-ads-overview/#latest-method-oauth-method`;
const CONTACT_US_LINK = 'https://support.branch.io/support/tickets/new';
const NEED_TO_BE_ADMIN_LINK = `${DLA_URL}/facebook-ads-faq/#i-see-a-warning-underneath-my-ad-account-saying-that-i-need-to-be-an-admin`;

export const getErrorCodeMap = (pathname) => {
  return {
    1: {
      text: (
        <span>
          We were unable to link our business account to your ad account. Please{' '}
          <Link href={CONTACT_US_LINK} testid="contact-support-link">
            contact us
          </Link>{' '}
          and include "Facebook MMP + unable to link business account" in the subject.
        </span>
      ),
      type: 'error',
      instrumentationText: '(#1): We were unable to link our business account to your ad account.',
    },
    2: {
      text: (
        <span>
          We were unable to link our system user to your ad account. Please{' '}
          <Link href={CONTACT_US_LINK} testid="contact-support-link">
            contact us
          </Link>{' '}
          and include "Facebook MMP + unable to link ad account" in the subject. Include all the ad account ids in the
          message.
        </span>
      ),
      type: 'error',
      instrumentationText: '(#2): We were unable to link our system user to your ad account.',
    },
    3: {
      text: (
        <span>
          This ad account is not linked to a business account, and you are not an admin on an account so could not be linked.
          Please go into the Facebook Account Manager and grant the Branch Ads Manager permission to report on your ads. Once
          you've done this, refresh the page to restart the authentication flow.{' '}
          <Link href={NEED_TO_BE_ADMIN_LINK} testid="need-to-be-admin-link">
            Learn more
          </Link>
          .
        </span>
      ),
      type: 'warning',
      instrumentationText:
        '(#3): This ad account is not linked to a business account, and you are not an admin on an account so could not be linked.',
    },
    4: {
      text: (
        <span>
          There are errors on the{' '}
          <Link
            to={{
              pathname,
              query: {
                tab: 'attribution_windows',
              },
            }}
            testid="attribution-windows-link"
          >
            attribution windows tab
          </Link>
          . Please correct these before proceeding.
        </span>
      ),
      type: 'error',
      instrumentationText: '(#4): there are errors on the ad attribution windows tab.',
    },
  };
};

export const CREDENTIALS_TOOLTIPS = {
  a_google_adwords: class extends React.PureComponent {
    render() {
      return (
        <div>
          <span>For more information on AdWords Conversion Labels and IDs, please read our documentation on </span>
          <Link href={ADWORDS_UAC_GUIDE_LINK} testid="tooltip__google-uac-docs-link">
            Universal App Campaigns
          </Link>
          <span> and </span>
          <Link href={ADWORDS_CONVERSION_GUIDE_LINK} testid="tooltip__google-conversion-docs-link">
            AdWords Conversion Tracking
          </Link>
          <span>.</span>
        </div>
      );
    }
  },
};

export const NoAdAccountsMessage = (props: { openReauthModal: () => void; adNetwork: string; machineName: string }) => (
  <div>
    <span>There are no ad accounts available based on the user you authenticated with {props.adNetwork}. Please </span>
    <Link onClick={props.openReauthModal} testid={`no-options__${props.machineName}-reauth-modal-link`}>
      reauthenticate
    </Link>
    <span> as a different user to start again.</span>
  </div>
);

export const WARNING_MODAL_REAUTH_TITLE = 'Are you sure you want to reauthenticate?';
export const WARNING_MODAL_CONFIRM_BUTTON = 'Yes, Reauthenticate';
export const getWarningModalReauthText = (partner) =>
  `If you reauthenticate, you could potentially break your integration ${
    partner ? `with ${partner}` : ''
  } if you complete the process with different
  credentials. Please only take this step after discussing with a
  Branch team member.`;

export const NoAppWarning = () => (
  <div>
    <i className="material-icons status_message_icon h_fz-body1 h_c-warning h_mend-5 h_mb-5">warning</i>
    No iTunes ID or Play ID found in your Configuration. Please configure your app in{' '}
    <Link to={'/configuration'} testid="no-app-link-settings-link">
      Configuration
    </Link>{' '}
    to continue.
  </div>
);

export const AppValidation = ({ mobileAppID, hasApp, className }) =>
  hasApp ? (
    <div className={classNames(className)}>
      <i className="material-icons status_message_icon h_fz-body1 h_c-success h_mend-5 h_mb-5">check_circle</i>
      {mobileAppID} found
    </div>
  ) : (
    <div>
      <i className="material-icons status_message_icon h_fz-body1 h_c-warning h_mend-5 h_mb-5">warning</i>
      No {mobileAppID} found. Please configure your app in{' '}
      <Link to={'/configuration'} testid="no-app-saved-link-settings-link">
        Configuration
      </Link>
      .
    </div>
  );

export const AdAccountsErrorMessage = (props: {
  openReauthModal: () => void;
  adNetwork: string;
  machineName: string;
  errorMessages?: string[];
}) => (
  <div>
    <span>An error occured while fetching ad accounts for {props.adNetwork}. Please </span>
    <Link onClick={props.openReauthModal} testid={`get-accounts__${props.machineName}-reauth-modal-link`}>
      reauthenticate
    </Link>
    <span> as a different user.</span>
    {props.errorMessages && (
      <ul>
        {props.errorMessages.map((errorMessage, index) => (
          <li key={index}>{errorMessage}</li>
        ))}
      </ul>
    )}
  </div>
);

export const AuthenticationErrorMessage = ({ show }) => (
  <span
    id="authentication-error-message"
    className={classNames('ad-partner-header__error-message', {
      show: show,
    })}
  >
    <i className="material-icons status_message_icon h_fz-body1 h_c-error h_mend-5 h_mb-5">error</i>
    error attempting to authenticate.
  </span>
);

export const getPartnerSettingsToggleText = (inheriting: boolean, orgName: string): React.ReactElement<any> =>
  inheriting ? (
    <span>
      <span className="h_fw-700">You are inheriting</span> this ad partner's settings from your app's parent organization,{' '}
      {orgName}. In order to edit settings or postbacks, please change this toggle to not inherit from the organization, or
      use the selector in the top left to make changes for the entire {orgName} organization.
    </span>
  ) : (
    <span>You are not inheriting this ad partner's settings from your app's parent organization, {orgName}.</span>
  );
